import {useContext, useRef, useState} from "react";

import {usePreHydrationEffect} from "@khanacademy/wonder-blocks-core";
import type {GuideState} from "@khan/guide-types";

import {GuideStateApiContext} from "./guide-state-api-context.ts";

/**
 * Provide reactive access to the guide state.
 *
 * Use this to access guide state when passing it to component props, hook
 * arguments, or the state changing affects whether an effect should trigger
 * or not.
 *
 * If your use case does not need to react to changes in the guide state and
 * the state is only accessed inside a function in a passive manner, consider
 * using the `useGuideStateApi` hook instead and the `getLatest` function from
 * its tuple. This is useful, for example, to grab the latest state before
 * invoking an asynchronous backend call with the latest state.
 */
export const useGuideState = <Key extends keyof GuideState>(
    key: Key,
): GuideState[Key] => {
    const setStateWillBeSafeRef = useRef(false);
    const GuideStateApi = useContext(GuideStateApiContext);
    const [currentValue, setCurrentValue] = useState<GuideState[Key]>(() => {
        const state = GuideStateApi.latest();
        return state[key];
    });

    usePreHydrationEffect(() => {
        setStateWillBeSafeRef.current = true;

        // Make sure we update our copy of the state to match the current
        // singleton state. This ensures we render again  with any state changes
        // that were made before we started listening for changes.
        setCurrentValue(GuideStateApi.latest()[key]);

        // Sign up for state changes. The returned function unsubscribes.
        const unsubscribe = GuideStateApi.on<Key>("changed", (changedKey) => {
            // We guard our set state call to make sure that we don't try
            // to do it when we've been unmounted.
            if (setStateWillBeSafeRef.current && key === changedKey) {
                const state = GuideStateApi.latest();
                const value = state[changedKey];
                // The state has changed, so we need to re-render the component tree.
                setCurrentValue(value);
            }
        });
        return () => {
            setStateWillBeSafeRef.current = false;
            unsubscribe();
        };
    });

    return currentValue;
};
